import * as React from 'react'
import { Modal } from 'antd'
import { colors } from 'web-makeup/dist/design-system/config/data/theme'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { redirect } from 'web-ui/dist/helpers/redirect'
import { isAdmin, isManager, isProducer } from 'web-fns/dist/auth'
import { Margin } from 'web-makeup/dist/design-system/styles/spacings'

import { AntModalStyles } from '../../../../../components/BrandDashboard/BrandDashboardSubBrands/SubBrands/SubBrands.style'
import DefaultButton from '../../../../../components/BrandDashboard/components/DefaultButton/DefaultButton'
import { Brand as BrandType } from '../../../../../typings/types'
import { ALL_ORGANISATIONS } from '../../../../../queries/AllOrganisations'
import { client as apolloClient } from '../../../../ApolloProvider/ApolloProvider'
import { DELETE_BRAND } from '../../../../../mutations/DeleteBrand'
import EnvironmentContext from '../../../../EnvironmentContext'
import { MERGE_BRAND } from '../../../../../mutations/MergeBrands'

import {
  PageTitle,
  WhiteBox,
  ConciergeFlexBox,
  ModalTitle,
  ModalContent,
  ButtonsWrapper,
  StyledSelect,
  ErrorMessage,
  UL,
  LI,
  AlignLeft,
  MergeAlert,
} from '../../BrandDashboardSettings.style'

interface BrandConciergeFormProps {
  brandProfile?: BrandType
  brandOrderUuid?: string
}

const BrandConciergeForm: React.FC<BrandConciergeFormProps> = ({
  brandOrderUuid,
  brandProfile,
}) => {
  const [modalVisible, setModalVisible] = React.useState(false)
  const [isDelete, setIsDelete] = React.useState(true)
  const [mergeBrandUuid, setMergeBrandUuid] = React.useState('')
  const [mergeError, setMergeError] = React.useState('')

  const { data: allOrganisations, refetch: refetchOrganisations } = useQuery(ALL_ORGANISATIONS, {
    variables: { keyword: '', includeOrganisationsWithoutProjects: true },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    client: apolloClient,
  })

  const [
    deleteBrand,
    { data: deleteBrandData, loading: isDeleteLoading, error: isDeleteBrandErrror },
  ] = useMutation(DELETE_BRAND, { client: apolloClient })

  const [
    mergeBrands,
    { data: mergeBrandData, loading: isMergeLoading, error: isMergeBrandErrror },
  ] = useMutation(MERGE_BRAND, {
    client: apolloClient,
    onError: err => setMergeError(err.graphQLErrors[0].errors[0].field),
  })
  const { tradingName, videoProjectsCount, children } = brandProfile || {}

  const { BRAND_BASE_URL } = React.useContext(EnvironmentContext) || ''
  const isAdminUser = isAdmin() || isProducer() || isManager()

  React.useEffect(
    () => {
      if (deleteBrandData && deleteBrandData.deleteBrand && deleteBrandData.deleteBrand.success) {
        setModalVisible(false)
        redirect(BRAND_BASE_URL)
      }
    },
    [deleteBrandData]
  )

  React.useEffect(
    () => {
      if (mergeBrandData && mergeBrandData.mergeBrands && mergeBrandData.mergeBrands.fullyMerged) {
        setModalVisible(false)
        redirect(`${BRAND_BASE_URL}/brand-dashboard/${mergeBrandData.mergeBrands.primaryBrandUuid}`)
      }
    },
    [mergeBrandData]
  )

  const { Option } = StyledSelect
  const teamMemberModalComponent = (
    <Modal
      visible={modalVisible}
      onCancel={() => {
        setModalVisible(false)
        setMergeBrandUuid('')
      }}
      width="46rem"
      footer={
        !isDelete && (
          <DefaultButton
            backgroundColor={colors.adaptBlue}
            color={colors.white}
            buttonHeight="3.25rem"
            padding="0 32px"
            fontSize="1rem"
            fontWeight="700"
            onClick={() => {
              if (mergeBrandUuid !== '') {
                mergeBrands({
                  variables: {
                    brandUuid: brandOrderUuid,
                    otherBrandUuid: mergeBrandUuid,
                  },
                })
              }
            }}
          >
            {isMergeLoading ? 'Loading...' : 'Merge'}
          </DefaultButton>
        )
      }
    >
      {isDelete ? (
        <>
          <ModalTitle>Are you sure you want to delete this brand?</ModalTitle>
          <ModalContent>
            Deleting this brand will permanently remove all associated data and cannot be undone.
            Please confirm if you want to proceed with this action.
          </ModalContent>
          {isDeleteBrandErrror && <ErrorMessage>Something went wrong</ErrorMessage>}
          <ButtonsWrapper>
            <DefaultButton
              rounded
              backgroundColor="transparent"
              color={colors.black1}
              buttonHeight="3.25rem"
              padding="0 32px"
              fontSize="1rem"
              fontWeight="700"
              onClick={() => setModalVisible(false)}
            >
              Cancel
            </DefaultButton>
            <DefaultButton
              backgroundColor={colors.alert}
              color={colors.white}
              buttonHeight="3.25rem"
              padding="0 32px"
              fontSize="1rem"
              fontWeight="700"
              onClick={() => {
                deleteBrand({
                  variables: {
                    brandUuid: brandOrderUuid,
                  },
                })
              }}
            >
              {isDeleteLoading ? 'Loading...' : 'Delete'}
            </DefaultButton>
          </ButtonsWrapper>
        </>
      ) : (
        <>
          <ModalTitle>You are about to merge {tradingName} with:</ModalTitle>
          <StyledSelect
            className={mergeBrandUuid === '' ? '' : 'filled'}
            name="ultimateParentBrandId"
            onSearch={value =>
              refetchOrganisations({ keyword: value, includeOrganisationsWithoutProjects: true })
            }
            onChange={value => setMergeBrandUuid(value)}
            showSearch
            value={mergeBrandUuid}
            placeholder="Search for brands"
            suffixIcon={
              <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
            }
            filterOption={(input, option) =>
              option.props.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {allOrganisations &&
              allOrganisations.allOrganisations &&
              allOrganisations.allOrganisations.length > 0 &&
              allOrganisations.allOrganisations.map(item => (
                <Option value={item.uuid}>{item.tradingName}</Option>
              ))}
          </StyledSelect>
          <Margin bottom size="small" />
          <ModalContent>
            <AlignLeft>This will execute the following actions:</AlignLeft>
            <UL>
              <LI>
                Use the most active organisation as the primary organisation that data gets merged
                into
              </LI>
              <LI>
                Copies over missing data fields to the primary (like website, phone, social media
                details, address, etc)
              </LI>
              <LI>Move all of the following to the primary organisation:</LI>
              <UL>
                <LI>Video Projects</LI>
                <LI>Employees</LI>
                <LI>Activities</LI>
                <LI>Attachments</LI>
                <LI>Comments</LI>
                <LI>Ratings</LI>
                <LI>Campaign Contacts</LI>
              </UL>
              <LI>
                Delete the old organisation if able to (some remaining data may need manual merging)
              </LI>
              <LI>Redirect you to the resulting merged organisation</LI>
            </UL>
            <MergeAlert>Caution!! This action cannot be undone, so use carefully!</MergeAlert>
            {isMergeBrandErrror && (
              <ErrorMessage>
                {mergeError === 'country'
                  ? `Cannot merge brands because they are based in different countries. Use sub-brands instead.`
                  : `Something went wrong`}
              </ErrorMessage>
            )}
          </ModalContent>
        </>
      )}
    </Modal>
  )

  return (
    <>
      <AntModalStyles />
      {teamMemberModalComponent}
      {isAdminUser &&
        videoProjectsCount === 0 &&
        (children && children.length === 0) && (
          <>
            <PageTitle>Concierge</PageTitle>
            <WhiteBox>
              <ConciergeFlexBox>
                <div>Delete Brand</div>
                <DefaultButton
                  rounded
                  backgroundColor={colors.alert}
                  color={colors.white}
                  buttonHeight="2.75rem"
                  padding="0 2.25rem"
                  fontSize="0.875rem"
                  fontWeight="600"
                  onClick={() => {
                    setModalVisible(true)
                    setIsDelete(true)
                  }}
                >
                  Delete
                </DefaultButton>
              </ConciergeFlexBox>
            </WhiteBox>
          </>
        )}
    </>
  )
}

export default BrandConciergeForm



// WEBPACK FOOTER //
// ./src/components/BrandDashboard/BrandDashboardSettings/forms/BrandConciergeForm/BrandConsiergeForm.tsx